/**
 * Der modale Dialog (Kopie aus Axis). Ein `div` mit `role="dialog"` (oder `"alertdialog"`) in einem
 * Portal an `document.body`, mit Scrim, Fokusfalle, Escape zum Schließen und Rückgabe des Fokus an das Element, das ihn
 * vorher hatte. Gestaltet in `components.css` (`.ksb-dialog*`).
 *
 * **Alert-Dialog (`modalType="alert"`):** ein Klick neben das Fenster schließt ihn **nicht** — die Rückfrage soll genau
 * den Fehlklick abfangen, der sie nötig macht. Schließen geht über Escape oder einen Knopf des Aufrufers.
 *
 * **Reine Darstellung.** Der Aufrufer hält `open` und entscheidet in `onClose`, ob geschlossen wird (er kann es
 * verweigern, solange eine Handlung läuft). Das Fenster **verschwindet beim Schließen nicht sofort**, sondern blendet
 * kurz aus (`EXIT_MS`, Paket: sichtbares Schließen statt Verschwinden); wer reduzierte Bewegung eingestellt hat, und
 * jede Umgebung ohne `matchMedia` (Tests), sieht es sofort verschwinden.
 *
 * **Schublade (`size="schublade"`):** dasselbe Fenster, von rechts über die ganze Höhe (Details zu einer Zeile der Liste),
 * mit `symbol` (z. B. Initialen) und `untertitel` in der Kopfzeile.
 *
 * Der erste Knopf mit `autoFocus` bekommt beim Öffnen den Fokus (so macht es die Rückfrage mit „Zurück" als
 * Vorzugsknopf), sonst das Fenster selbst.
 */

import { X } from '../icons';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { classNames } from './classNames';

/** So lange blendet der Dialog beim Schließen aus (passt zu `ksb-dialog--leaving` in `components.css`). */
export const DIALOG_EXIT_MS = 180;

export interface DialogProps {
  open: boolean;
  /** Escape, Klick auf das Scrim (nicht beim Alert-Dialog) und das Kreuz. */
  onClose: () => void;
  title: ReactNode;
  /** `alert`: Rolle `alertdialog`, das Scrim schließt nicht. */
  modalType?: 'modal' | 'alert';
  size?: 'md' | 'lg' | 'full' | 'schublade';
  /** Symbol vor dem Titel, z. B. Initialen (für die Schublade). */
  symbol?: ReactNode;
  /** Zeile unter dem Titel. */
  untertitel?: ReactNode;
  /** Fußzeile, rechtsbündig (Knöpfe). */
  actions?: ReactNode;
  /** Kein Kreuz in der Titelzeile (wenn die Fußzeile schließt). */
  hideClose?: boolean;
  /** Zusätzliche Klasse auf dem Fenster. */
  className?: string;
  /** Zusätzliche Klasse auf dem Inhaltsbereich. */
  bodyClassName?: string;
  children: ReactNode;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia !== 'function' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  open,
  onClose,
  title,
  modalType = 'modal',
  size = 'md',
  symbol,
  untertitel,
  actions,
  hideClose = false,
  className,
  bodyClassName,
  children,
}: DialogProps) {
  const titleId = useId();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  // Öffnen: sofort einhängen. Schließen: kurz ausblenden, dann aushängen. Abweichend von Axis wird der Zustand
  // beim Wechsel von `open` im Rendern angepasst statt im Effekt (Regel react-hooks/set-state-in-effect).
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setMounted(true);
      setLeaving(false);
    } else if (mounted) {
      if (prefersReducedMotion()) setMounted(false);
      else setLeaving(true);
    }
  }

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => {
      setMounted(false);
      setLeaving(false);
    }, DIALOG_EXIT_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  // Fokus beim Öffnen setzen und beim Aushängen zurückgeben.
  useLayoutEffect(() => {
    if (!mounted) return undefined;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const surface = surfaceRef.current;
    // React setzt `autoFocus` beim Einhängen selbst (ohne ein `autofocus`-Attribut im DOM zu hinterlassen). Hat ein Kind
    // den Fokus schon, bleibt er dort — sonst würde er hier auf das Fenster zurückgesetzt.
    if (surface !== null && surface.contains(document.activeElement))
      return () => previous?.focus?.();
    const preferred = surface?.querySelector<HTMLElement>('[data-autofocus]');
    (preferred ?? surface)?.focus();
    return () => previous?.focus?.();
  }, [mounted]);

  // Die Seite dahinter scrollt nicht mit.
  useEffect(() => {
    if (!mounted) return undefined;
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = before;
    };
  }, [mounted]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      // Fokusfalle: Tab springt vom letzten zum ersten Element und umgekehrt.
      const surface = surfaceRef.current;
      if (surface === null) return;
      const focusable = Array.from(surface.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        surface.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === surface)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!mounted) return null;

  return createPortal(
    <div
      className={classNames(
        'ksb-dialog-scrim',
        size === 'schublade' && 'ksb-dialog-scrim--schublade',
        leaving && 'ksb-dialog-scrim--leaving',
      )}
      onMouseDown={(event) => {
        if (modalType === 'modal' && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={surfaceRef}
        role={modalType === 'alert' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={classNames(
          'ksb-dialog',
          `ksb-dialog--${size}`,
          leaving && 'ksb-dialog--leaving',
          className,
        )}
        onKeyDown={onKeyDown}
      >
        <div className="ksb-dialog__head">
          {symbol}
          {untertitel === undefined ? (
            <h2 className="ksb-dialog__title" id={titleId}>
              {title}
            </h2>
          ) : (
            <div className="ksb-dialog__titelblock">
              <h2 className="ksb-dialog__title" id={titleId}>
                {title}
              </h2>
              <p className="ksb-dialog__untertitel ksb-body-s">{untertitel}</p>
            </div>
          )}
          {!hideClose && (
            <button type="button" className="ksb-round" aria-label="Schließen" onClick={onClose}>
              <X weight="light" size={24} />
            </button>
          )}
        </div>
        <div className={classNames('ksb-dialog__body', bodyClassName)}>{children}</div>
        {actions !== undefined && <div className="ksb-dialog__foot">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}
